<script setup lang="ts">
  import { computed } from 'vue';
  import type { TrainingMatrixPositionRow } from '@/entities/Training';

  const props = defineProps<{
    positions: TrainingMatrixPositionRow[];
  }>();

  const HEADER_ROW = 1;

  const rowSpanFor = (position: TrainingMatrixPositionRow) => Math.max(position.documents.length, 1);

  // Each document gets its own grid row (cargo cell spans them) so a wrapped, multi-line
  // title grows its row and drags the matching prefix cell along with it, keeping the two
  // columns from drifting apart — stacking them as plain divs inside a single grid cell
  // (the previous approach) let title/prefix heights diverge independently.
  const rowStarts = computed(() => {
    const starts: number[] = [];
    let nextRow = HEADER_ROW + 1;
    for (const position of props.positions) {
      starts.push(nextRow);
      nextRow += rowSpanFor(position);
    }
    return starts;
  });
</script>

<template>
  <div
    class="grid grid-cols-[max-content_max-content_minmax(0,1fr)]"
    data-testid="training-matrix-report-table"
  >
    <div class="border-b border-gray-02 py-3 pl-6 pr-7 text-xs font-semibold text-gray-07">
      Cargos
    </div>
    <div class="border-b border-gray-02 py-3 pr-7 text-xs font-semibold text-gray-07">
      Prefixo
    </div>
    <div class="border-b border-gray-02 py-3 pr-6 text-xs font-semibold text-gray-07">
      Título
    </div>

    <template
      v-for="(position, index) in positions"
      :key="position.id"
    >
      <div
        :data-testid="`report-row-${position.id}`"
        :style="{ gridRow: `${rowStarts[index]} / span ${rowSpanFor(position)}`, gridColumn: 1 }"
        :class="[
          'flex items-center whitespace-nowrap py-3 pl-6 pr-7 text-xs leading-5 text-gray-07',
          index < positions.length - 1 ? 'border-b border-gray-02' : '',
        ]"
      >
        {{ position.name }}
      </div>

      <template v-if="position.documents.length > 0">
        <div
          v-for="(document, docIndex) in position.documents"
          :key="`${position.id}-prefix-${document.id}`"
          :data-testid="`report-prefix-${position.id}`"
          :style="{ gridRow: rowStarts[index] + docIndex, gridColumn: 2 }"
          :class="[
            'py-3 pr-7 text-xs leading-5 text-gray-07',
            docIndex === position.documents.length - 1 && index < positions.length - 1
              ? 'border-b border-gray-02'
              : '',
          ]"
        >
          {{ document.code }}
        </div>
        <div
          v-for="(document, docIndex) in position.documents"
          :key="`${position.id}-title-${document.id}`"
          :data-testid="`report-title-${position.id}`"
          :style="{ gridRow: rowStarts[index] + docIndex, gridColumn: 3 }"
          :class="[
            'py-3 pr-6 text-xs leading-5 text-gray-07',
            docIndex === position.documents.length - 1 && index < positions.length - 1
              ? 'border-b border-gray-02'
              : '',
          ]"
        >
          {{ document.name }}
        </div>
      </template>
      <template v-else>
        <div
          :data-testid="`report-prefix-${position.id}`"
          :style="{ gridRow: rowStarts[index], gridColumn: 2 }"
          :class="[
            'py-3 pr-7 text-xs leading-5 text-gray-07',
            index < positions.length - 1 ? 'border-b border-gray-02' : '',
          ]"
        >
          —
        </div>
        <div
          :data-testid="`report-title-${position.id}`"
          :style="{ gridRow: rowStarts[index], gridColumn: 3 }"
          :class="[
            'py-3 pr-6 text-xs leading-5 text-gray-07',
            index < positions.length - 1 ? 'border-b border-gray-02' : '',
          ]"
        >
          —
        </div>
      </template>
    </template>
  </div>
</template>
